// RigSentinel — Drilling Fluid Management Module (Phase RCE-3)
//
// KIND REGISTRY
// -------------
// The Drilling Fluid Management family parallels the Daily Operations
// family (`lib/dailyOps/kinds.js`). RCE-3 ships only the Mud Program +
// Daily Mud Report; the remaining 8 capabilities are reserved with
// `enabled: false` so the architecture is locked in without expanding
// scope.
//
// Module Ownership Declaration — every fluid kind carries `owns / consumes
// / publishes / overrides` per the RCE-2 architectural principle.

import { CONTEXT_VERSION } from "@/lib/dailyOps/kinds";

export { CONTEXT_VERSION };

function ownership({ owns = [], consumes = [], publishes = [], overrides = [] } = {}) {
  return { owns, consumes, publishes, overrides };
}

export const FLUID_KINDS = Object.freeze({
  mudProgram: {
    id: "mudProgram",
    label: "Mud Program",
    enabled: true,
    isMasterRecord: true,
    storageKeySuffix: "program",
    ownership: ownership({
      owns: [
        "mud.programId", "mud.fluidBasis",
        // Planned tri-state properties (TARGET state).
        "mud.weightInPpg", "mud.plasticViscosityCp", "mud.yieldPointLbf100ft2",
        "mud.gelStrength10sLbf100ft2", "mud.gelStrength10minLbf100ft2",
        "mud.fluidLossMl30min", "mud.pH",
        "mud.sandContentPct", "mud.oilWaterRatio",
        "mud.electricalStabilityV", "mud.htHpFluidLossMl",
      ],
      consumes: [
        "well.id", "well.name", "well.holeSection",
        "project.operator",
      ],
      publishes: [
        "mud.programId", "mud.fluidBasis", "mud.weightInPpg",
        "mud.plasticViscosityCp", "mud.yieldPointLbf100ft2",
        "mud.gelStrength10sLbf100ft2", "mud.gelStrength10minLbf100ft2",
        "mud.fluidLossMl30min", "mud.pH", "mud.sandContentPct",
        "mud.oilWaterRatio", "mud.electricalStabilityV", "mud.htHpFluidLossMl",
      ],
      overrides: [],   // master records override via their own editor
    }),
  },
  dailyMudReport: {
    id: "dailyMudReport",
    label: "Daily Mud Report",
    enabled: true,
    isMasterRecord: false,
    storageKeySuffix: "dmr",
    ownership: ownership({
      owns: [
        "fluidDoc.reportDate", "fluidDoc.preparedBy",
        "fluidDoc.volumes", "fluidDoc.treatments",
        "fluidDoc.solidsControl", "fluidDoc.gainsLosses",
        "fluidDoc.notes", "fluidDoc.recommendations",
      ],
      consumes: [
        "project.operator", "project.client",
        "rig.name",
        "well.name", "well.holeSection", "well.currentMdFt", "well.currentTvdFt",
        "mud.programId", "mud.fluidBasis",
        "mud.weightInPpg", "mud.plasticViscosityCp", "mud.yieldPointLbf100ft2",
        "mud.gelStrength10sLbf100ft2", "mud.gelStrength10minLbf100ft2",
        "mud.fluidLossMl30min", "mud.pH",
        "mud.sandContentPct", "mud.oilWaterRatio",
        "mud.electricalStabilityV", "mud.htHpFluidLossMl",
      ],
      publishes: [
        // The DMR is the canonical "current" reading for every mud
        // property. Future RCE phases will subscribe to these.
        "mud.weightInPpg", "mud.plasticViscosityCp", "mud.yieldPointLbf100ft2",
        "mud.gelStrength10sLbf100ft2", "mud.gelStrength10minLbf100ft2",
        "mud.fluidLossMl30min", "mud.pH",
        "mud.sandContentPct", "mud.oilWaterRatio",
        "mud.electricalStabilityV", "mud.htHpFluidLossMl",
      ],
      overrides: [
        // DMR may set a document-scope override for any of these.
        "mud.weightInPpg", "mud.plasticViscosityCp", "mud.yieldPointLbf100ft2",
        "mud.gelStrength10sLbf100ft2", "mud.gelStrength10minLbf100ft2",
        "mud.fluidLossMl30min", "mud.pH",
        "mud.sandContentPct", "mud.oilWaterRatio",
        "mud.electricalStabilityV", "mud.htHpFluidLossMl",
      ],
    }),
  },

  // -------------------------------------------------------------------
  // Reserved kinds — architectural placeholders only. RCE-3 does not
  // implement them, but the architecture acknowledges their existence
  // so future phases plug in without breaking the contract.
  // -------------------------------------------------------------------
  mudProperties:    { id: "mudProperties",    label: "Mud Properties",         enabled: false, isMasterRecord: false, storageKeySuffix: "props",     ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
  mudTreatments:    { id: "mudTreatments",    label: "Mud Treatments",         enabled: false, isMasterRecord: false, storageKeySuffix: "treat",     ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
  mudInventory:     { id: "mudInventory",     label: "Mud Inventory",          enabled: false, isMasterRecord: true,  storageKeySuffix: "inv",       ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
  volumeAccounting: { id: "volumeAccounting", label: "Volume Accounting",      enabled: false, isMasterRecord: false, storageKeySuffix: "vol",       ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
  solidsControl:    { id: "solidsControl",    label: "Solids Control",         enabled: false, isMasterRecord: false, storageKeySuffix: "solids",    ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
  chemicalTreatments:{id: "chemicalTreatments",label:"Chemical Treatments",    enabled: false, isMasterRecord: false, storageKeySuffix: "chemtreat", ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
  lostCirculationMaterial:{id:"lostCirculationMaterial",label:"Lost Circulation Material",enabled: false, isMasterRecord: false, storageKeySuffix:"lcm", ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
  fluidHistory:     { id: "fluidHistory",     label: "Fluid History",          enabled: false, isMasterRecord: false, storageKeySuffix: "history",   ownership: ownership({ owns: [], consumes: [], publishes: [], overrides: [] }) },
});

export const FLUID_KIND_IDS = Object.freeze(Object.keys(FLUID_KINDS));

export function isKnownFluidKind(k) { return !!FLUID_KINDS[k]; }
export function isFluidKindEnabled(k) { return !!FLUID_KINDS[k]?.enabled; }
export function getFluidKind(k) { return FLUID_KINDS[k] || null; }
export function listEnabledFluidKinds() {
  return FLUID_KIND_IDS.filter((k) => FLUID_KINDS[k].enabled);
}
